import React, { useCallback, useSyncExternalStore } from 'react';
import type { ChatContactUser } from '@/components/Messenger/sidebar/ChatSidebar';
import { getUserDisplayName } from '@/components/Messenger/sidebar/chatSidebarDisplay';
import {
  getMessengerDraft,
  subscribeMessengerDraft,
} from '@/components/Messenger/cache/messengerDraftsStore';
import { formatMessengerComposerDraftSubtitle } from '@/components/Messenger/shared/messengerComposerDraftPreview';
import { useMessengerThreadByPeerId } from '@/hooks/useMessengerThreadByPeerId';
import { useMessengerSidebarUnreadViewportSubtract } from '@/hooks/useMessengerSidebarUnreadViewportSubtract';
import { usePresence } from '@/hooks/usePresence';
import {
  highlightPlainText,
  SEARCH_HIGHLIGHT_CLASS_MESSENGER,
} from '@/utils/highlightSearchTerms';
import { getMessengerThreadListSubtitle } from '@/components/Messenger/shared/messengerThreadListPreview';

export interface ChatSidebarContactRowProps {
  user: ChatContactUser;
  isActive: boolean;
  pinned: boolean;
  searchHighlightWords: readonly string[];
  currentUserId: string | null;
  onSelect: (contact: { type: 'direct'; peerId: string; threadId: string; peerName: string }) => void;
  onOpenContextMenu: (e: React.MouseEvent<HTMLButtonElement>, userId: string) => void;
}

export const ChatSidebarContactRow = React.memo(function ChatSidebarContactRow({
  user,
  isActive,
  pinned,
  searchHighlightWords,
  currentUserId,
  onSelect,
  onOpenContextMenu,
}: ChatSidebarContactRowProps) {
  const thread = useMessengerThreadByPeerId(user.id);
  const viewportSubtract = useMessengerSidebarUnreadViewportSubtract();
  const presence = usePresence(user.id);
  const displayName = getUserDisplayName(user);
  const draftTargetId = thread?.id ?? '';
  const draft = useSyncExternalStore(
    useCallback(
      (onChange) => subscribeMessengerDraft('thread', draftTargetId, onChange),
      [draftTargetId]
    ),
    () => (draftTargetId ? getMessengerDraft('thread', draftTargetId) : undefined),
    () => undefined
  );
  const subtitle =
    formatMessengerComposerDraftSubtitle(draft) ??
    getMessengerThreadListSubtitle(thread, currentUserId);
  const rawUnread = thread?.unread_count ?? 0;
  const sub =
    viewportSubtract.peerId === user.id && viewportSubtract.count > 0
      ? viewportSubtract.count
      : 0;
  const unread = Math.max(0, rawUnread - sub);

  const hl =
    searchHighlightWords.length > 0
      ? (s: string) => highlightPlainText(s, searchHighlightWords, SEARCH_HIGHLIGHT_CLASS_MESSENGER)
      : null;

  const handleClick = useCallback(() => {
    onSelect({
      type: 'direct',
      peerId: user.id,
      threadId: thread?.id ?? '',
      peerName: thread?.peer_name ?? displayName,
    });
  }, [onSelect, user.id, thread?.id, thread?.peer_name, displayName]);

  return (
    <button
      type="button"
      className={`chatSidebar__item ${isActive ? 'chatSidebar__item_active' : ''}`}
      onClick={handleClick}
      onContextMenu={(e) => onOpenContextMenu(e, user.id)}
    >
      <span className="chatSidebar__itemContent">
        <span className="chatSidebar__itemLabelRow">
          {presence.online && <span className="chatSidebar__onlineDot" aria-hidden />}
          <span className="chatSidebar__itemLabel">{hl ? hl(displayName) : displayName}</span>
        </span>
        <span className="chatSidebar__itemSubtitle">{hl ? hl(subtitle) : subtitle}</span>
      </span>
      {unread > 0 ? (
        <span className="messengerUnreadBadge">
          {unread > 99 ? '99+' : String(unread)}
        </span>
      ) : null}
      {pinned ? (
        <i className="fa-solid fa-thumbtack chatSidebar__rowPinFa" aria-hidden title="Закреплено" />
      ) : null}
    </button>
  );
});
